import { useTranslation } from 'react-i18next'
import { twMerge } from 'tailwind-merge'
import { LESSON_WORD_EMOJI, type LessonWordId } from './lessonVocabulary'

/**
 * Duolingo's new-word drill: "Which one of these is 'cat'?" over picture cards
 * labelled in the target language. Emoji stand in for Duolingo's illustrations.
 * Selecting a card only arms the CHECK button; the lesson decides correctness.
 */
export default function ImageChoiceExercise({
  correctId,
  locked,
  onSelect,
  options,
  prompt,
  selected,
  vocabulary,
}: {
  correctId: LessonWordId
  /** Set once the answer is checked, so the cards stop responding. */
  locked: boolean
  onSelect: (option: LessonWordId) => void
  options: LessonWordId[]
  /** The word to find, in the learner's own language. */
  prompt: string
  selected?: LessonWordId
  vocabulary: Record<LessonWordId, string>
}) {
  const { t } = useTranslation()

  return (
    <div className="flex grow flex-col">
      <p className="text-duo-primary text-xs font-extrabold tracking-[0.12em] uppercase">
        {t('pages.onboarding.v2.lesson.newWord')}
      </p>

      <h1 className="duo-heading mt-2">
        {t('pages.onboarding.v2.lesson.imageChoiceTitle', { word: prompt })}
      </h1>

      <div className="mt-6 grid grid-cols-2 gap-3">
        {options.map((option) => {
          const isSelected = selected === option
          const isCorrect = option === correctId

          return (
            <button
              className={twMerge(
                'border-duo-line flex flex-col items-center gap-3 rounded-2xl border-2 border-b-4 bg-white px-3 py-5 transition-colors duration-150 last:odd:col-span-2 last:odd:w-[calc(50%-0.375rem)] last:odd:justify-self-center',
                isSelected && 'border-duo-primary bg-duo-primary-soft',
                locked && isCorrect && 'border-duo-green bg-duo-green-soft',
                locked &&
                  isSelected &&
                  !isCorrect &&
                  'border-duo-red bg-duo-red-soft',
                !locked && 'active:mt-0.5 active:border-b-2',
              )}
              disabled={locked}
              key={option}
              onClick={() => onSelect(option)}
              type="button"
            >
              <span aria-hidden="true" className="text-6xl leading-none">
                {LESSON_WORD_EMOJI[option]}
              </span>

              <span
                className={twMerge(
                  'text-duo-ink text-base font-bold',
                  isSelected && 'text-duo-primary',
                  locked && isCorrect && 'text-duo-green-text',
                  locked && isSelected && !isCorrect && 'text-duo-red-text',
                )}
              >
                {vocabulary[option]}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
